import { useModals } from "@gusarov-studio/rubik-ui";
import { useFieldArray, useWatch, type Control } from "react-hook-form";
import { dialogNames } from "../../dialogs/dialogNames";
import { DEFAULT_TOOL_CODE, DEFAULT_TOOL_INPUT_SCHEMA } from "../constants";
import type { ToolValues } from "../schemas/ToolSchema";

interface ToolsFormValues {
  tools: ToolValues[];
}

function createTool(): ToolValues {
  return {
    id: crypto.randomUUID(),
    name: "",
    description: "",
    input_schema: DEFAULT_TOOL_INPUT_SCHEMA,
    code: DEFAULT_TOOL_CODE,
  };
}

/**
 * The `tools` field array of any form that has one (a prompt's editor, a
 * chat), plus the dialog plumbing for editing one.
 *
 * A tool is only written back to the form when its dialog is submitted, so an
 * abandoned draft leaves the form untouched — including a brand-new tool,
 * which is never appended until it is saved. Deleting an existing tool is
 * offered from the dialog footer and removes it from the form immediately.
 *
 * `onChange` receives the list as it stands after each of those writes, for a
 * form that persists its tools on the spot rather than on submit.
 */
export function useToolsFieldArray<T extends ToolsFormValues>(
  control: Control<T>,
  { onChange }: { onChange?: (tools: ToolValues[]) => void } = {},
) {
  // The field array only ever touches `tools`, so the rest of `T` is irrelevant
  // here; narrowing lets react-hook-form type the path without a generic dance.
  const toolsControl = control as unknown as Control<ToolsFormValues>;
  const modals = useModals();
  const { append, update, remove } = useFieldArray({ control: toolsControl, name: "tools" });
  const tools = useWatch({ control: toolsControl, name: "tools" }) ?? [];

  const openTool = ({
    tool,
    index,
    onSubmit,
  }: {
    tool: ToolValues;
    index: number | null;
    onSubmit: (value: ToolValues) => void;
  }) => {
    modals.open(dialogNames.TOOL_BUILDER, {
      tool,
      reservedNames: tools
        .filter((_candidate, candidateIndex) => candidateIndex !== index)
        .map((candidate) => candidate.name)
        .filter(Boolean),
      onSubmit,
      onDelete:
        index === null
          ? undefined
          : () => {
              remove(index);
              onChange?.(tools.filter((_candidate, candidateIndex) => candidateIndex !== index));
            },
    });
  };

  return {
    tools,
    addTool: () =>
      openTool({
        tool: createTool(),
        index: null,
        onSubmit: (value) => {
          append(value);
          onChange?.([...tools, value]);
        },
      }),
    editTool: (index: number) =>
      openTool({
        tool: tools[index],
        index,
        onSubmit: (value) => {
          update(index, value);
          onChange?.(
            tools.map((candidate, candidateIndex) =>
              candidateIndex === index ? value : candidate,
            ),
          );
        },
      }),
  };
}
